import { ChevronsRight } from 'lucide-react';
import { useEffect } from 'react';

import { DrawerHeader, Inline, Stack, Typography } from '@crossnokaye/ux-common';

import { TableColumnConfig, TableRowConfig } from '@repo/sdk/models/components';

import { DiscardChangesDialog } from '@/components/change-dialogs/discard-changes-dialog';
import { Button } from '@/ux-common/components/button/button';

import { EquipmentBulkEditColumnSelector } from './equipment-bulk-edit-column-selector';
import { EquipmentBulkEditSetting } from './equipment-bulk-edit-setting';
import { useEquipmentBulkEditPushoutState } from './use-equipment-bulk-edit-pushout-state';

interface EquipmentBulkEditPushoutProps {
  columns: TableColumnConfig[];
  onClose: () => void;
  onFocusedColumnChange?: (columnId: string | null) => void;
  onSelectedColumnIdsChange?: (columnIds: string[]) => void;
  rows: TableRowConfig[];
  selectedRowIds: string[];
}

export const EquipmentBulkEditPushout = ({
  columns,
  onClose,
  onFocusedColumnChange,
  onSelectedColumnIdsChange,
  rows,
  selectedRowIds,
}: EquipmentBulkEditPushoutProps) => {
  const {
    selectedColumnIds,
    setSelectedColumnIds,
    selectedColumns,
    bulkValues,
    hasChanges,
    handleBulkValueChange,
    dirtyColumnIds,
    disabledColumnIds,
    disabledColumnReasons,
    handleApply,
    handleCloseAttempt,
    handleDiscardAttempt,
    handleDiscardColumns,
    discardDialog,
  } = useEquipmentBulkEditPushoutState({ columns, rows, selectedRowIds, onClose });

  // Notify parent when selectedColumnIds changes
  useEffect(() => {
    onSelectedColumnIdsChange?.(selectedColumnIds);
  }, [selectedColumnIds, onSelectedColumnIdsChange]);

  return (
    <div className="h-full flex flex-col border-l border-t border-ckds-border-disabled">
      {/* Header */}
      <DrawerHeader className="py-4 px-5 border-b border-ckds-border-tier2">
        <Inline className="items-center justify-between w-full" gap="default">
          <Inline gap="default" className="items-center">
            <Button
              className="btn btn-tier2 btn-md btn-square"
              onClick={handleCloseAttempt}
              aria-label="Close bulk edit"
            >
              <ChevronsRight className="btn-icon" aria-hidden="true" />
            </Button>
            <Typography variant="h2">Bulk Edit</Typography>
          </Inline>
          <EquipmentBulkEditColumnSelector
            columns={columns}
            selectedColumnIds={selectedColumnIds}
            onSelectionChange={setSelectedColumnIds}
            dirtyColumnIds={dirtyColumnIds}
            disabledColumnIds={disabledColumnIds}
            disabledColumnReasons={disabledColumnReasons}
            onDiscardColumns={handleDiscardColumns}
          />
        </Inline>
      </DrawerHeader>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-5">
        <Stack gap="default">
          {selectedColumns.map((column) => (
            <EquipmentBulkEditSetting
              key={column.columnId}
              column={column}
              onSettingBlur={() => onFocusedColumnChange?.(null)}
              onSettingFocus={(columnId) => onFocusedColumnChange?.(columnId)}
              onValueChange={handleBulkValueChange}
              rows={rows}
              selectedRowIds={selectedRowIds}
              value={bulkValues[column.columnId]}
            />
          ))}
        </Stack>
      </div>

      {/* Footer */}
      <div className="border-t border-ckds-border-disabled p-stack-sm">
        <Inline className="justify-between px-2" gap="default">
          <Button onClick={handleDiscardAttempt} className="btn btn-tier2 flex-1">
            Discard Changes
          </Button>
          <Button onClick={handleApply} disabled={!hasChanges} className="btn btn-brand flex-1">
            Apply Changes
          </Button>
        </Inline>
      </div>

      <DiscardChangesDialog
        open={discardDialog.open}
        onOpenChange={discardDialog.onOpenChange}
        onConfirm={discardDialog.onConfirm}
      />
    </div>
  );
};
